import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, type FounderEstado, type FounderPedido, type FounderTier } from '../../../api';
import { existe, idiomaAtual, numero, t } from '../../../i18n/idioma';
import { useIdioma } from '../../../i18n/useIdioma';
import {
  abrirJanelaCheckout, fecharJanela, levarAoCheckout, LinkDoCheckout, motivoDaRecusa, novaChaveCartao
} from '../../../components/LojaCartao';
import { montarAntiRobo, type AntiRobo } from '../antiRobo';
import { Selo } from './desenhos';
import { chaveDoErro, imagem, nivel, nomeDo, packDe, reais, TIERS, tierDe, valorDe } from './dados';

/**
 * A compra de um Founder Pack (techspec-founders-pack §06.2). Sem conta: o pack fica no e-mail, que é provado
 * por um código de 6 números. O site guarda o token do código e o segredo do pedido só em memória, nunca em
 * storage; fechou a janela, o recibo chega por e-mail.
 */
export type Compra = { readonly tipo: 'comprar'; readonly tier: FounderTier } | { readonly tipo: 'subir' };

type Etapa = 'email' | 'codigo' | 'escolher' | 'semPack' | 'pagar' | 'pix' | 'cartao' | 'fim';
const ETAPAS_BARRA: readonly Etapa[] = ['email', 'codigo', 'pagar', 'pix', 'fim'];
const EMAIL_MAX = 254;
const ABERTOS = ['criando', 'aguardando', 'processando'];

function emailValido(bruto: string): boolean {
  const e = bruto.trim();
  if (e.length < 5 || e.length > EMAIL_MAX || /\s/.test(e)) return false;
  const at = e.indexOf('@');
  return at >= 1 && at === e.lastIndexOf('@') && at !== e.length - 1 && e.slice(at + 1).includes('.');
}

/** Só para a tela: `v***@example.com`. */
function mascarar(email: string): string {
  const [local, dominio] = email.split('@');
  if (!dominio || !local) return '***';
  return `${local[0]}${'*'.repeat(Math.max(3, Math.min(6, local.length - 1)))}@${dominio}`;
}

function textoDoErro(e: unknown): string {
  if (e instanceof ApiError) {
    const chave = chaveDoErro(e.code);
    if (chave && existe(chave)) return t(chave);
    if (e.status === 429) return t('landing.fund.erro.limite');
    if (e.status === 0) return t('landing.fund.erro.rede');
    if (e.message) return e.message;
  }
  return t('landing.fund.erro.rede');
}

function relogio(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function FounderModal({ estado, compra, chaveSite, onFechar }: {
  estado: FounderEstado;
  compra: Compra;
  chaveSite: string | null;
  onFechar(): void;
}) {
  const idioma = useIdioma();
  const [etapa, setEtapa] = useState<Etapa>('email');
  const [tier, setTier] = useState<FounderTier | null>(compra.tipo === 'comprar' ? compra.tier : null);
  const [email, setEmail] = useState('');
  const [pre, setPre] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [atual, setAtual] = useState<FounderTier | null>(null);
  const [aberto, setAberto] = useState(false);
  const [metodo, setMetodo] = useState<'pix' | 'cartao'>(estado.metodos.includes('pix') ? 'pix' : 'cartao');
  const [aceite, setAceite] = useState(false);
  const [maior, setMaior] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [pedido, setPedido] = useState<FounderPedido | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  const [copiado, setCopiado] = useState(false);
  /** O navegador bloqueou a janela do Mercado Pago: a etapa do cartão mostra o link em destaque. */
  const [bloqueada, setBloqueada] = useState(false);
  const token = useRef('');
  const segredo = useRef('');
  const chave = useRef(novaChaveCartao());
  const caixa = useRef<HTMLDivElement>(null);
  const caixaAntiRobo = useRef<HTMLDivElement>(null);
  const antiRobo = useRef<Promise<AntiRobo> | null>(null);
  const origem = useRef<Element | null>(document.activeElement);

  // Esc fecha; ao fechar, o foco volta a quem abriu.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', tecla);
    const quem = origem.current;
    return () => {
      window.removeEventListener('keydown', tecla);
      if (quem instanceof HTMLElement) quem.focus({ preventScroll: true });
    };
  }, [onFechar]);

  // A cada etapa, o foco vai para o primeiro campo (ou para a janela).
  useEffect(() => {
    const el = caixa.current?.querySelector<HTMLElement>('input:not([type=checkbox]), button.lp-btn--ouro, button.lp-btn--fund');
    (el ?? caixa.current)?.focus({ preventScroll: true });
    setErro(null);
  }, [etapa]);

  // Anti-robô só na etapa do e-mail (é ele que dispara o envio do código).
  useEffect(() => {
    const el = caixaAntiRobo.current;
    if (!chaveSite || !el || etapa !== 'email') return;
    const montado = montarAntiRobo(el, chaveSite, idioma);
    montado.catch(() => {});
    antiRobo.current = montado;
    return () => {
      antiRobo.current = null;
      void montado.then(w => w.remover()).catch(() => {});
    };
  }, [chaveSite, idioma, etapa]);

  const valor = tier ? valorDe(estado, tier, atual) : 0;
  const vivo = pedido && ABERTOS.includes(pedido.estado);

  // Relógio do Pix e da janela do cartão.
  useEffect(() => {
    if ((etapa !== 'pix' && etapa !== 'cartao') || !vivo) return;
    const id = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [etapa, vivo]);

  // Consulta o pedido enquanto ele está aberto: o webhook do Mercado Pago confirma no servidor.
  useEffect(() => {
    if (!pedido || !vivo || !segredo.current) return;
    let ativo = true;
    const consultar = () => {
      void api.founderPedido(pedido.id, segredo.current).then(p => {
        if (ativo) receber(p);
      }).catch(() => { /* a próxima tenta de novo */ });
    };
    const id = window.setInterval(consultar, 4000);
    return () => { ativo = false; window.clearInterval(id); };
  }, [pedido?.id, vivo]);

  const receber = (p: FounderPedido) => {
    setPedido(atualP => (atualP && atualP.id === p.id ? {
      ...atualP, ...p,
      pixCopiaCola: p.pixCopiaCola ?? (p.estado === 'aguardando' ? atualP.pixCopiaCola : null),
      pixQrBase64: p.pixQrBase64 ?? (p.estado === 'aguardando' ? atualP.pixQrBase64 : null),
      checkout: p.checkout ?? (p.estado === 'aguardando' ? atualP.checkout : null)
    } : p));
    if (!ABERTOS.includes(p.estado)) setEtapa('fim');
  };

  // ── ações ──────────────────────────────────────────────────────────────────────────────────────────────

  const pedirCodigo = async () => {
    if (ocupado) return;
    const e = email.trim();
    if (!emailValido(e)) { setErro(t('landing.fund.erro.email')); return; }
    setErro(null);
    setOcupado(true);
    try {
      let tokenAntiRobo: string | undefined;
      if (chaveSite) {
        const w = await antiRobo.current;
        if (!w) throw new ApiError(t('landing.fund.erro.antiRobo'), 400, 'founder.antiRobo');
        tokenAntiRobo = await w.obterToken().catch(() => { throw new ApiError(t('landing.fund.erro.antiRobo'), 400, 'founder.antiRobo'); });
      }
      await api.founderCodigo({ email: e, idioma: idiomaAtual(), antiRobo: tokenAntiRobo });
      setEmail(e);
      setCodigo('');
      setEtapa('codigo');
    } catch (err) {
      setErro(textoDoErro(err));
    } finally {
      setOcupado(false);
      void antiRobo.current?.then(w => w.reiniciar()).catch(() => {});
    }
  };

  const conferir = async () => {
    if (ocupado) return;
    if (!/^\d{6}$/.test(codigo)) { setErro(t('landing.fund.erro.codigoFormato')); return; }
    setOcupado(true);
    setErro(null);
    try {
      const r = await api.founderConferir({ email, codigo });
      token.current = r.token;
      setAtual(r.tier);
      setAberto(r.pagamentoAberto);
      // Só depois do código o site diz se o e-mail já é fundador (não dá para descobrir e-mails pela vitrine).
      if (compra.tipo === 'subir') setEtapa(r.tier ? 'escolher' : 'semPack');
      else if (r.tier && nivel(r.tier) >= nivel(tier)) setEtapa('escolher');
      else setEtapa('pagar');
    } catch (err) {
      setErro(textoDoErro(err));
    } finally {
      setOcupado(false);
    }
  };

  const criar = async () => {
    if (!tier) return null;
    return api.founderPedir({
      email,
      token: token.current,
      tier,
      metodo,
      chave: chave.current,
      politicaVersao: estado.politica.versao,
      aceite,
      maior18: maior,
      preRegistro: pre,
      idioma: idiomaAtual()
    });
  };

  const tratarFalha = (err: unknown) => {
    // Token do código vencido: volta ao e-mail. Recusado de vez (não 502): a próxima tentativa é outro pedido.
    if (err instanceof ApiError && err.code === 'founder.token') {
      setEtapa('email');
      window.setTimeout(() => setErro(textoDoErro(err)), 0);
      return;
    }
    if (err instanceof ApiError && err.status !== 502) chave.current = novaChaveCartao();
    setErro(textoDoErro(err));
  };

  const pagar = async () => {
    if (ocupado || !aceite || !maior) return;
    // Cartão (Checkout Pro): a janela do Mercado Pago abre já no clique; o endereço chega com o pedido.
    const janela = metodo === 'cartao' ? abrirJanelaCheckout() : null;
    setOcupado(true);
    setErro(null);
    setBloqueada(false);
    try {
      const p = await criar();
      if (!p) { fecharJanela(janela); return; }
      segredo.current = p.segredo ?? '';
      setPedido(p);
      if (!ABERTOS.includes(p.estado)) {
        fecharJanela(janela);
        setEtapa('fim');
      } else if (p.metodo === 'cartao') {
        setBloqueada(!levarAoCheckout(janela, p.checkout));
        setEtapa('cartao');
      } else setEtapa('pix');
    } catch (err) {
      fecharJanela(janela);
      tratarFalha(err);
    } finally {
      setOcupado(false);
    }
  };

  const cancelarPix = async () => {
    if (!pedido || ocupado) return;
    setOcupado(true);
    try {
      receber(await api.founderCancelar(pedido.id, segredo.current));
    } catch (err) {
      setErro(textoDoErro(err));
    } finally {
      setOcupado(false);
    }
  };

  const copiar = async () => {
    if (!pedido?.pixCopiaCola) return;
    try {
      await navigator.clipboard.writeText(pedido.pixCopiaCola);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch { /* o campo continua selecionável */ }
  };

  // ── tela ───────────────────────────────────────────────────────────────────────────────────────────────

  const tv = tier ? tierDe(tier) : null;
  const pack = tier ? packDe(estado, tier) : undefined;
  const pago = etapa === 'fim' && pedido?.estado === 'pago';
  const titulo = pago ? t('landing.fund.m.obrigado')
    : compra.tipo === 'subir' && !tier ? t('landing.fund.m.subir')
      : tier ? t('landing.fund.m.titulo', { nome: nomeDo(tier) }) : t('landing.fund.m.subir');
  const sub = pago ? '' : atual && tier ? t('landing.fund.m.subSubir') : t('landing.fund.m.sub');
  const barra = ETAPAS_BARRA.indexOf(
    etapa === 'escolher' || etapa === 'semPack' ? 'codigo' : etapa === 'cartao' ? 'pix' : etapa);

  const resumo = tier && tv ? (
    <div className="lp-f-resumo">
      <img src={imagem(`monta-${tier}`)} alt="" />
      <div>
        <b>{nomeDo(tier)}</b>
        <small>{t('landing.fund.m.resumo', { vip: numero(pack?.vipDias ?? 0), ouro: numero(pack?.ouro ?? 0) })}</small>
      </div>
      <strong>{reais(valor)}</strong>
    </div>
  ) : null;
  const aviso = erro ? <p className="lp-aviso" role="alert">{erro}</p> : null;
  const teste = estado.simulado
    ? <span className="lp-f-teste">{t('pag.simulado.chip')}</span>
    : estado.ambiente === 'teste' ? <span className="lp-f-teste">{t('landing.fund.m.teste')}</span> : null;

  let corpo: ReactNode;
  if (etapa === 'email') {
    corpo = (
      <form className="lp-f-form" noValidate onSubmit={e => { e.preventDefault(); void pedirCodigo(); }}>
        {resumo}
        <p className="lp-f-aviso-email">
          <b>{t('landing.fund.m.emailForte')}</b> {t('landing.fund.m.emailAviso')}
          {compra.tipo === 'subir' ? ` ${t('landing.fund.m.emailSubir')}` : ''}
        </p>
        <div className="lp-campo">
          <label htmlFor="lp-f-email">{t('landing.form.email')}</label>
          <input id="lp-f-email" type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} maxLength={EMAIL_MAX}
            placeholder={t('landing.form.emailExemplo')} value={email} disabled={ocupado}
            onChange={e => setEmail(e.target.value)} aria-invalid={erro ? true : undefined} />
        </div>
        {compra.tipo === 'comprar' && (
          <label className="lp-aceite">
            <input type="checkbox" checked={pre} disabled={ocupado} onChange={e => setPre(e.target.checked)} />
            <span>{t('landing.fund.m.pre')}</span>
          </label>
        )}
        {aviso}
        <button type="submit" className="lp-btn lp-btn--ouro" disabled={ocupado}>
          {ocupado ? <><span className="lp-carregando" aria-hidden="true" /> {t('landing.form.enviando')}</> : t('landing.fund.m.mandar')}
        </button>
        <p className="lp-miudo">{t('landing.fund.m.emailMiudo')}</p>
        <div className="lp-antirobo" ref={caixaAntiRobo} />
      </form>
    );
  } else if (etapa === 'codigo') {
    const [antes, depois] = t('landing.fund.m.codigoEnviado', { email: '\u0000' }).split('\u0000');
    corpo = (
      <form className="lp-f-form" noValidate onSubmit={e => { e.preventDefault(); void conferir(); }}>
        <p>{antes}<b>{mascarar(email)}</b>{depois}</p>
        <div className="lp-campo lp-f-codigo">
          <label htmlFor="lp-f-cod">{t('landing.fund.m.codigo')}</label>
          <input id="lp-f-cod" inputMode="numeric" maxLength={6} autoComplete="one-time-code" placeholder="000000"
            value={codigo} disabled={ocupado} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        </div>
        {aviso}
        <button type="submit" className="lp-btn lp-btn--ouro" disabled={ocupado}>
          {ocupado ? <span className="lp-carregando" aria-hidden="true" /> : null} {t('landing.fund.m.conferir')}
        </button>
        <p className="lp-miudo">
          {t('landing.fund.m.naoChegou')}{' '}
          <a href="#fundadores" onClick={e => { e.preventDefault(); setEtapa('email'); }}>{t('landing.fund.m.outroEmail')}</a>
        </p>
      </form>
    );
  } else if (etapa === 'semPack') {
    corpo = (
      <div className="lp-f-form">
        <p>{t('landing.fund.m.semPack')}</p>
        <button type="button" className="lp-btn lp-btn--ouro" onClick={onFechar}>{t('landing.fund.m.verPacks')}</button>
      </div>
    );
  } else if (etapa === 'escolher') {
    const acima = TIERS.filter(x => nivel(x.id) > nivel(atual) && packDe(estado, x.id)?.ativo);
    corpo = (
      <div className="lp-f-form">
        <p>{t('landing.fund.m.jaE', { nome: atual ? nomeDo(atual) : '' })}</p>
        {acima.length
          ? acima.map(x => (
            <button key={x.id} type="button" className="lp-btn lp-btn--fund lp-f-largo"
              onClick={() => { setTier(x.id); setEtapa('pagar'); }}>
              <Selo metal={x.metal} /> {t('landing.fund.m.subirPara', { nome: nomeDo(x.id), v: reais(valorDe(estado, x.id, atual)) })}
            </button>
          ))
          : <p className="lp-miudo">{t('landing.fund.m.maior')}</p>}
      </div>
    );
  } else if (etapa === 'pagar' && tier) {
    corpo = (
      <form className="lp-f-form" noValidate onSubmit={e => { e.preventDefault(); void pagar(); }}>
        {resumo}
        {atual && (
          <p className="lp-f-aviso-email">
            {t('landing.fund.m.sobe', { de: nomeDo(atual), para: nomeDo(tier), vip: numero(pack?.vipDias ?? 0), ouro: numero(pack?.ouro ?? 0) })}
          </p>
        )}
        {aberto && <p className="lp-aviso">{t('landing.fund.erro.aberto')}</p>}
        <div>
          <span className="lp-miudo">{t('landing.fund.m.ficaNo')}</span>
          <div className="lp-f-mono">{mascarar(email)}</div>
        </div>
        <div className="lp-f-metodos" role="group" aria-label={t('landing.fund.m.metodo')}>
          {estado.metodos.includes('pix') && (
            <button type="button" aria-pressed={metodo === 'pix'} onClick={() => setMetodo('pix')}>
              <b>Pix</b><small>{t('landing.fund.m.pixNota')}</small>
            </button>
          )}
          {estado.metodos.includes('cartao') && (
            <button type="button" aria-pressed={metodo === 'cartao'} onClick={() => setMetodo('cartao')}>
              <b>{t('landing.fund.m.cartao')}</b><small>{t('landing.fund.m.cartaoNota', { n: estado.parcelasMax })}</small>
            </button>
          )}
        </div>
        <label className="lp-aceite">
          <input type="checkbox" checked={aceite} onChange={e => setAceite(e.target.checked)} />
          <span>{t('landing.fund.m.aceite')}</span>
        </label>
        {estado.politica.texto && <p className="lp-miudo lp-f-politica">{estado.politica.texto}</p>}
        <label className="lp-aceite">
          <input type="checkbox" checked={maior} onChange={e => setMaior(e.target.checked)} />
          <span>{t('landing.fund.m.maior18')}</span>
        </label>
        {aviso}
        <button type="submit" className="lp-btn lp-btn--ouro" disabled={ocupado || !aceite || !maior}>
          {ocupado ? <span className="lp-carregando" aria-hidden="true" /> : null}{' '}
          {t(metodo === 'cartao' ? 'landing.fund.m.pagarCartao' : 'landing.fund.m.pagarPix', { v: reais(valor) })}
        </button>
      </form>
    );
  } else if (etapa === 'pix' && pedido) {
    const resta = pedido.expiraEm ? Date.parse(pedido.expiraEm) - agora : 0;
    corpo = (
      <div className="lp-f-form">
        <div className="lp-f-qr">
          {pedido.pixQrBase64
            ? <img src={`data:image/png;base64,${pedido.pixQrBase64}`} alt={t('landing.fund.m.qrAlt')} />
            : <div className="lp-f-qr__vazio" aria-hidden="true" />}
          <div className="lp-f-qr__txt">
            {teste}
            <p>{estado.simulado ? t('pag.simulado.pix') : t('landing.fund.m.pixDica')}</p>
            {pedido.expiraEm && <p className="lp-miudo">{t('landing.fund.m.expira', { t: relogio(resta) })}</p>}
            <p className="lp-f-espera"><span className="lp-carregando" aria-hidden="true" /> {t('landing.fund.m.aguardando')}</p>
          </div>
        </div>
        {pedido.pixCopiaCola && (
          <div className="lp-f-copia">
            <input readOnly value={pedido.pixCopiaCola} aria-label={t('landing.fund.m.copiaCola')} onFocus={e => e.currentTarget.select()} />
            <button type="button" className="lp-btn" onClick={() => { void copiar(); }}>{copiado ? t('landing.fund.m.copiado') : t('landing.fund.m.copiar')}</button>
          </div>
        )}
        {aviso}
        <p className="lp-miudo lp-f-centro">{t('landing.fund.m.podeFechar')}</p>
        <button type="button" className="lp-btn" disabled={ocupado} onClick={() => { void cancelarPix(); }}>{t('landing.fund.m.cancelarPix')}</button>
      </div>
    );
  } else if (etapa === 'cartao' && pedido) {
    const resta = pedido.expiraEm ? Date.parse(pedido.expiraEm) - agora : 0;
    corpo = (
      <div className="lp-f-form">
        {resumo}
        {teste}
        <p>{estado.simulado ? t('pag.checkout.simulado') : bloqueada ? t('pag.checkout.bloqueada') : t('pag.checkout.dica')}</p>
        <LinkDoCheckout url={pedido.checkout} classe={bloqueada ? 'lp-btn lp-btn--ouro lp-f-largo' : 'lp-btn lp-f-largo'} />
        {pedido.expiraEm && <p className="lp-miudo">{t('landing.fund.m.expira', { t: relogio(resta) })}</p>}
        <p className="lp-f-espera"><span className="lp-carregando" aria-hidden="true" /> {t('pag.checkout.aguardando')}</p>
        {aviso}
        <p className="lp-miudo">{t('pag.checkout.seguro')}</p>
        <p className="lp-miudo lp-f-centro">{t('landing.fund.m.podeFechar')}</p>
        <button type="button" className="lp-btn" disabled={ocupado} onClick={() => { void cancelarPix(); }}>{t('pag.checkout.desistir')}</button>
      </div>
    );
  } else if (pago && pedido) {
    const nome = nomeDo(pedido.tier);
    const [antes, depois] = t('landing.fund.m.recibo', { email: '\u0000' }).split('\u0000');
    corpo = (
      <div className="lp-f-pronto" role="status">
        <img src={imagem(`monta-${pedido.tier}`)} alt="" />
        <h3>{t('landing.fund.m.voce', { nome })}</h3>
        <p>{antes}<b>{mascarar(email)}</b>{depois}</p>
        <ul>
          <li><b>{t('landing.fund.m.agoraForte')}</b> {pre ? t('landing.fund.m.agoraPre') : t('landing.fund.m.agora')}</li>
          <li><b>{t('landing.fund.m.contaForte')}</b> {t('landing.fund.m.conta')}</li>
          <li><b>{t('landing.fund.m.loginForte')}</b> {t('landing.fund.m.login')}</li>
        </ul>
        <button type="button" className="lp-btn lp-btn--ouro lp-f-largo" onClick={onFechar}>{t('landing.fund.m.fechar')}</button>
      </div>
    );
  } else {
    const est = pedido?.estado ?? 'falhou';
    const recusado = est === 'recusado';
    const chaveEstado = `pag.estado.${est}`;
    corpo = (
      <div className="lp-f-form" role="status">
        <h3>{existe(chaveEstado) ? t(chaveEstado) : t('pag.estado.falhou')}</h3>
        <p>{recusado ? motivoDaRecusa(pedido?.detalhe) : existe(`${chaveEstado}Dica`) ? t(`${chaveEstado}Dica`) : ''}</p>
        {recusado && <p className="lp-miudo">{t('pag.recusaNadaCobrado')}</p>}
        <div className="lp-f-acoes">
          <button type="button" className="lp-btn" onClick={onFechar}>{t('landing.fund.m.fechar')}</button>
          <button type="button" className="lp-btn lp-btn--ouro" onClick={() => {
            chave.current = novaChaveCartao();
            setPedido(null);
            setEtapa('pagar');
          }}>{t('landing.fund.m.tentar')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="lp-f-veu" onMouseDown={e => { if (e.target === e.currentTarget && !vivo) onFechar(); }}>
      <div className="lp-gump lp-f-modal" role="dialog" aria-modal="true" aria-labelledby="lp-f-m-titulo" ref={caixa} tabIndex={-1}>
        <div className="lp-gump__cab lp-f-modal__cab">
          <div>
            <h2 id="lp-f-m-titulo">{titulo}</h2>
            {sub ? <p>{sub}</p> : null}
            <div className="lp-f-etapas" aria-hidden="true">
              {ETAPAS_BARRA.map((e, i) => <i key={e} className={barra >= i ? 'is-on' : undefined} />)}
            </div>
          </div>
          <button type="button" className="lp-f-x" aria-label={t('landing.fund.m.fechar')} onClick={onFechar}>×</button>
        </div>
        <div className="lp-gump__corpo" aria-live="polite">{corpo}</div>
      </div>
    </div>
  );
}

